import React, { useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import Progress from '../../components/Progress';
import { SetSecondProps } from './interfaces';
import { useTranslation } from 'react-i18next';
import { ScreenEnum } from '../../enums';
import RadioButtons from '../../components/RadioButtons';
import Option from '../../components/Option';
import addParam from '../../utils/addParam';
import AppStateContext from '../../AppStateProvider/context';

// Extend the Window interface to include getScreens method
declare global {
	interface Window {
		getScreens?: () => Promise<Screen[]>;
	}
}

interface Screen {
	availLeft: number;
	availTop: number;
}

export default function SetSecond(props: SetSecondProps) {
	const { state, dispatch } = useContext(AppStateContext);
	const { t } = useTranslation();
	const params = new URLSearchParams(window.location.search);
	const chromebox =
		params.get('device-type') === 'Chromebox' ||
		params.get('chromebox') === 'true' ||
		window.location.href.includes('chrome');
	const [showSecond, setShowSecond] = useState<boolean>(props.showSecond);
	const [orientation, setOrientation] = useState<string>(
		state.orientation || 'default'
	);

	useEffect(() => {
		if (!showSecond) {
			addParam('orientation', null, true);
		}
	}, [showSecond]);

	const url = new URL(`${window.location.origin}/index.html`);
	url.searchParams.set('label', 'true');
	if (orientation) url.searchParams.set('orientation', orientation);

	const popupRef = useRef<Window | null>(null);
	const openWindow = async () => {
		try {
			if ('getScreenDetails' in window) {
				const screens = await (window as any).getScreenDetails();

				if (screens.screens.length > 1) {
					const secondScreen = screens.screens[1];
					const width = secondScreen.width;
					const height = secondScreen.height;
					const left = secondScreen.left;
					const top = secondScreen.top;

					popupRef.current = window.open(
						url + '&redirect=false',
						'label',
						`popup=yes,width=${width},height=${height},left=${left},top=${top}`
					);
				} else {
					alert('Only one screen detected.');
				}
			} else {
				alert('Window Management API not supported.');
			}
		} catch (error) {
			console.error('Error using Window Management API:', error);
		}
	};

	const redirect = () => {
		if (popupRef.current) {
			popupRef.current.location.href = `${window.location.origin}/${state.brand}/index.html?label=true`;
			popupRef.current.location.replace(
				`${window.location.origin}/${state.brand}/index.html?label=true`
			);
		}
	};

	window.addEventListener('redirect-label', redirect);

	useEffect(() => {
		if (chromebox) {
			if (showSecond && (!popupRef.current || popupRef.current?.closed)) {
				openWindow();
			} else {
				popupRef.current?.close();
				popupRef.current = null;
			}
		}
	}, [showSecond]);

	function handleClick(value: boolean | string) {
		const isToggle = typeof value === 'boolean';

		// SHOW / HIDE SECOND SCREEN

		if (isToggle) {
			const nextShowSecond = value;

			setShowSecond(nextShowSecond);

			if (nextShowSecond && orientation) {
				dispatch({
					type: 'SET_ORIENTATION',
					value: orientation,
				});

				addParam('orientation', orientation, true);
			}

			if (!nextShowSecond) {
				dispatch({
					type: 'SET_ORIENTATION',
					value: null,
				});

				addParam('orientation', null, true);
			}

			return;
		}

		// ORIENTATION CHANGE

		const nextOrientation = value;

		setOrientation(nextOrientation);

		dispatch({
			type: 'SET_ORIENTATION',
			value: nextOrientation || null,
		});

		addParam('orientation', nextOrientation, true);
	}

	return (
		<>
			<div className='top'>
				<div className='set-second'>
					{props.backBtn(ScreenEnum.hardwareCheck)}
					<h1>{t('SetSecondHeadline')}</h1>
					<p className='p1'>{t('SetSecondText')}</p>
					<Progress
						screen={ScreenEnum.setSecond}
						setScreen={props.setScreen}
					/>
					<Option
						selected={showSecond}
						text={t('SetSecondShow')}
						onClick={() => {
							const next = !showSecond;

							setShowSecond(next);

							dispatch({
								type: 'SET_SHOW_SECOND_SCREEN',
								value: next,
							});
						}}
						className='show-second'
						checkMark={true}
					/>
					{!chromebox && (
						<RadioButtons
							headline={t('SetSecondRotation')}
							disabled={!showSecond}
							options={[
								{
									selected: orientation === 'default',
									text: '0°',
									value: 'default',
								},
								{
									selected: orientation === 'right',
									text: '90°',
									value: 'right',
								},
								{
									selected: orientation === 'down',
									text: '180°',
									value: 'down',
								},
								{
									selected: orientation === 'left',
									text: '270°',
									value: 'left',
								},
							]}
							onClick={(value: string) => handleClick(value)}
						/>
					)}
				</div>
			</div>
			<div className='buttons'>
				{!chromebox ? (
					<>
						<button
							className='white'
							onClick={() =>
								props.setScreen(ScreenEnum.softwareCheck)
							}
							disabled={!showSecond || orientation === ''}
						>
							{t('SetSecondConfirm')}
						</button>

						<button
							className='black'
							onClick={() =>
								props.setScreen(ScreenEnum.softwareCheck)
							}
						>
							{t('ButtonSkip')}
						</button>
					</>
				) : (
					<button
						className='white'
						onClick={() =>
							props.setScreen(ScreenEnum.softwareCheck)
						}
					>
						{t('ButtonConfirm')}
					</button>
				)}
			</div>
		</>
	);
}
